import {
  Archive,
  Bell,
  Delete,
  LayersFront,
  Link,
  SolidPlay,
  SolidStop,
} from '@factorialco/f0-react/icons/app'
import { type EngagementSurveyDetailTabsQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { ResourceHeadersPropsWithActions } from 'modules/core/lib/navigation/types'

import { formatDate, formatDateTime } from 'modules/engagement_surveys/lib/dates'
import {
  formRecurrenceMapper,
  mapRecurrenceTypeFromBackend,
} from 'modules/engagement_surveys/lib/formRecurrenceMapper'
import { formStatusMapper } from 'modules/engagement_surveys/lib/formStatusMapper'

export function buildSurveyHeaderProps(
  data: EngagementSurveyDetailTabsQuery
): ResourceHeadersPropsWithActions {
  const survey = data?.surveys.survey
  const canCreateSurvey = data?.surveys._policies.surveysCreateSurvey.can ?? false

  if (!survey) {
    return { title: '' }
  }

  const form = survey.form
  const formStatus = form.status
  const status = formStatusMapper(formStatus)
  const canEdit = survey._policies?.edit.can ?? false

  const primaryAction = (() => {
    if (!canEdit) {
      return undefined
    }

    if (formStatus === 'draft' || formStatus === 'finished') {
      return {
        key: 'publish',
        label: i18n.t('common.publish'),
        icon: SolidPlay,
      }
    }

    return undefined
  })()

  const otherActions = (() => {
    if (!canEdit) {
      return undefined
    }

    const all = []

    if (formStatus === 'active' || formStatus === 'scheduled') {
      all.push({
        key: 'share-link',
        label: i18n.t('surveys.link'),
        icon: Link,
      })
    }

    if (formStatus === 'active') {
      all.push({
        key: 'remind-respondents',
        label: i18n.t('surveys.remind_respondents_dialog.send_reminder'),
        icon: Bell,
      })
    }

    if (canCreateSurvey) {
      all.push({
        key: 'duplicate',
        label: i18n.t('common.duplicate'),
        icon: LayersFront,
      })
    }

    if (formStatus === 'finished') {
      all.push({
        key: 'archive',
        label: i18n.t('common.archive'),
        icon: Archive,
        critical: true,
      })
    }

    if (formStatus === 'draft' || formStatus === 'scheduled') {
      all.push({
        key: 'delete',
        label: i18n.t('common.delete'),
        icon: Delete,
        critical: true,
      })
    }

    return all.length > 0 ? all : undefined
  })()

  const secondaryActions = (() => {
    const all = []

    if (!canEdit) {
      return
    }

    if (formStatus === 'scheduled') {
      all.push({
        key: 'unschedule',
        label: i18n.t('surveys.header.actions.unschedule'),
        variant: 'critical' as const,
        icon: SolidStop,
      })
    }

    if (formStatus === 'active') {
      all.push({
        key: 'finish-survey',
        label: i18n.t('surveys.header.actions.finish_survey'),
        variant: 'critical' as const,
        icon: SolidStop,
      })
    }

    return all.length > 0 ? all : undefined
  })()

  const latestCycle = form.schedule?.cyclesConnection?.nodes?.at(0)
  const surveyFinished = ['finished', 'archived'].includes(formStatus)

  const metadata = (() => {
    const all = []

    if (latestCycle) {
      all.unshift({
        key: 'publish-date',
        label: i18n.t('surveys.header.metadata.publish_date'),
        value: {
          content: formatDateTime(latestCycle.startsAt),
          type: 'text' as const,
        },
      })
    }

    const displayRecurrenceType = mapRecurrenceTypeFromBackend(form.recurrenceType)
    all.push({
      key: 'recurrence',
      label: i18n.t('surveys.header.metadata.recurrence'),
      value: {
        content: formRecurrenceMapper(displayRecurrenceType),
        type: 'text' as const,
      },
    })

    if (form.nextRunDate && !surveyFinished) {
      all.push({
        key: 'next-launch',
        label: i18n.t('surveys.header.metadata.next_launch'),
        value: { content: formatDateTime(form.nextRunDate), type: 'text' as const },
      })
    }

    if (!surveyFinished) {
      all.push({
        key: 'ends-at',
        label: i18n.t('surveys.header.metadata.ends_at'),
        value: {
          content: form.endsAt ? formatDate(form.endsAt) : 'Never ends',
          type: 'text' as const,
        },
      })
    }

    if (displayRecurrenceType !== 'onetime') {
      all.push({
        key: 'days-to-answer',
        label: i18n.t('surveys.header.metadata.days_to_answer'),
        value: {
          content: form.configuration?.daysToAnswer?.toString() || '-',
          type: 'text' as const,
        },
      })
    }

    all.push({
      key: 'questions',
      label: i18n.t('surveys.header.metadata.questions'),
      value: {
        content: (form.elementsConnection?.totalCount ?? 0)?.toString() || '-',
        type: 'text' as const,
      },
    })

    return all
  })()

  return {
    title: form.title,
    description: form.description ?? undefined,
    status: {
      label: status.label,
      text: status.label,
      variant: status.status,
    },
    metadata,
    primaryAction,
    secondaryActions,
    otherActions,
  }
}
